import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createHttpClient } from '@shared/adapters/HttpClient';
import { apiClientWH } from '@shared/services/ApiClient';
import { RelocationProcessApiAdapter } from '../adapters/RelocationProcessApiAdapter';
import type { RelocationDetail, RelocationLine } from '../models';

const RELOCATION_QUERY_KEY = 'relocation-process-detail';

export const relocationQueryKey = (id: number) => [RELOCATION_QUERY_KEY, id];

export const useRelocationQuery = (id: number) => {
  const queryClient = useQueryClient();
  const httpClient = createHttpClient(apiClientWH);
  const adapter = new RelocationProcessApiAdapter(httpClient);

  const query = useQuery<RelocationDetail>({
    queryKey: relocationQueryKey(id),
    queryFn: () => adapter.getRelocation(id),
    enabled: id > 0,
    staleTime: 2 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const updateRelocation = (updated: RelocationDetail) => {
    queryClient.setQueryData<RelocationDetail>(relocationQueryKey(id), updated);
  };

  const appendLine = (line: RelocationLine) => {
    queryClient.setQueryData<RelocationDetail>(relocationQueryKey(id), (prev) => {
      if (!prev) return prev;
      const lines = Array.isArray(prev.lines) ? prev.lines : [];
      return { ...prev, lines: [...lines, line] };
    });
  };

  return {
    relocation: query.data ?? null,
    loading: query.isLoading,
    error: query.error instanceof Error ? query.error.message : (query.error as string | null),
    refetch: query.refetch,
    updateRelocation,
    appendLine,
  };
};
